import React, { useEffect, useState } from 'react'
import type { AuthContextValue } from './AuthProvider'
import { useAuth } from './AuthProvider'
import { getLoginAuthorizationParams, storeAuthRedirectUrl } from './util/login'

/**
 * Automatically redirects to login if the user is unauthenticated
 */
export default function Authorizer({
  children
}: {
  children: React.ReactNode | ((args: AuthContextValue) => React.ReactNode)
}) {
  const ctx = useAuth()
  const { auth0Client, session, authenticate, isAuthenticating } = ctx
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (!redirecting && !session) {
      setRedirecting(true)
      storeAuthRedirectUrl()
      auth0Client.loginWithRedirect({
        authorizationParams: getLoginAuthorizationParams()
      })
    }
    // eslint-disable-next-line
  }, [redirecting, session])

  // refresh session if possible on page load
  useEffect(() => {
    if (!isAuthenticating) {
      authenticate({ ignoreCache: false }).catch((e) => {
        // error codes that require user to login again
        const needsLoginErrors = ['invalid_grant', 'login_required', 'access_denied', 'unauthorized']

        if (needsLoginErrors.includes(e.error)) {
          // only stash when we're actually redirecting — a transient failure (timeout,
          // network, mfa_required) doesn't reach /login-callback, so the value would
          // never be consumed and would hijack the next login on this origin
          storeAuthRedirectUrl()
          auth0Client.loginWithRedirect({
            authorizationParams: getLoginAuthorizationParams()
          })
        } else {
          console.error(e)
        }
      })
    }
    // eslint-disable-next-line
  }, [])

  return <>{typeof children === 'function' ? children(ctx) : children}</>
}
